/**
 * CollectionSwatchPicker
 *
 * Custom Element that powers the color swatch strip on collection card grids.
 *
 * Responsibilities:
 *  - On swatch change  → update card image (src + srcset) and link href
 *  - On swatch hover   → preview variant image (desktop / mouse only)
 *  - On swatch leave   → restore selected variant image
 *  - On card click     → allow navigation unless the click was on the swatch strip
 *
 * All variant data is embedded as data-* attributes on each
 * .card-swatch-strip__item element by Liquid at render time.
 * Zero network requests after initial page load.
 */
class CollectionSwatchPicker extends HTMLElement {
  /** @type {string|null} image URL of the currently selected swatch */
  #selectedImageUrl = null;

  /** @type {string|null} alt text of the currently selected swatch */
  #selectedImageAlt = null;

  /** @type {boolean} true while a hover preview is active */
  #isHovering = false;

  connectedCallback() {
    this.#init();
  }

  // ─── Initialisation ────────────────────────────────────────────────────────

  #init() {
    this.cardImage  = this.querySelector('[data-swatch-image]');
    this.cardLink   = this.querySelector('[data-swatch-link]');
    this.swatchStrip = this.querySelector('.card-swatch-strip');

    if (!this.cardImage || !this.cardLink || !this.swatchStrip) return;

    // Seed selected state from the first checked input
    const firstItem = this.swatchStrip.querySelector(
      '.card-swatch-strip__input:checked'
    )?.closest('.card-swatch-strip__item');

    if (firstItem) {
      this.#selectedImageUrl = firstItem.dataset.imageUrl || null;
      this.#selectedImageAlt = firstItem.dataset.imageAlt || null;
      // Align card link to the first (checked) variant on load
      if (firstItem.dataset.variantUrl) {
        this.cardLink.href = firstItem.dataset.variantUrl;
      }
    }

    // Swatch change (radio)
    this.swatchStrip.addEventListener('change', this.#onSwatchChange.bind(this));

    // Hover preview – bind to each label individually so we can read its item
    this.swatchStrip
      .querySelectorAll('.card-swatch-strip__label')
      .forEach((label) => {
        label.addEventListener('pointerenter', this.#onSwatchHover.bind(this));
        label.addEventListener('pointerleave', this.#onSwatchLeave.bind(this));
      });

    // Prevent swatch strip clicks from triggering any card-level click handler
    this.swatchStrip.addEventListener('click', (e) => e.stopPropagation());
  }

  // ─── Event handlers ────────────────────────────────────────────────────────

  /**
   * Called when a radio swatch is selected.
   * Updates image, link, and persists the new selected state.
   * @param {Event} event
   */
  #onSwatchChange(event) {
    const input = /** @type {HTMLInputElement} */ (event.target);
    if (input.type !== 'radio') return;

    const item = input.closest('.card-swatch-strip__item');
    if (!item) return;

    this.#isHovering = false;
    this.#selectedImageUrl = item.dataset.imageUrl || null;
    this.#selectedImageAlt = item.dataset.imageAlt || null;

    this.#updateImage(
      item.dataset.imageUrl,
      item.dataset.imageAlt,
      item.dataset.imageWidth,
      item.dataset.imageHeight,
      false
    );
    this.#updateLink(item.dataset.variantUrl);
  }

  /**
   * Called on pointerenter over a swatch label (mouse only).
   * Shows a temporary preview of that variant's image.
   * @param {PointerEvent} event
   */
  #onSwatchHover(event) {
    if (event.pointerType !== 'mouse') return;

    const label = /** @type {HTMLElement} */ (event.currentTarget);
    const item  = label.closest('.card-swatch-strip__item');
    if (!item) return;

    const previewUrl = item.dataset.imageUrl;
    if (!previewUrl || previewUrl === this.#selectedImageUrl) return;

    this.#isHovering = true;
    this.#updateImage(
      previewUrl,
      item.dataset.imageAlt,
      item.dataset.imageWidth,
      item.dataset.imageHeight,
      true   // isPreview → no loading class, instant swap
    );
  }

  /**
   * Called on pointerleave from a swatch label (mouse only).
   * Restores the image of the currently selected swatch.
   * @param {PointerEvent} event
   */
  #onSwatchLeave(event) {
    if (event.pointerType !== 'mouse') return;
    if (!this.#isHovering) return;

    this.#isHovering = false;

    if (!this.#selectedImageUrl) return;

    const checkedItem = this.swatchStrip
      .querySelector('.card-swatch-strip__input:checked')
      ?.closest('.card-swatch-strip__item');

    this.#updateImage(
      this.#selectedImageUrl,
      this.#selectedImageAlt || (checkedItem?.dataset.imageAlt ?? null),
      checkedItem?.dataset.imageWidth ?? null,
      checkedItem?.dataset.imageHeight ?? null,
      true   // isPreview → instant, no loading class
    );
  }

  // ─── DOM updaters ──────────────────────────────────────────────────────────

  /**
   * Swaps the card's main image src + srcset.
   *
   * @param {string|null|undefined} imageUrl   - Base CDN URL (no width param)
   * @param {string|null|undefined} imageAlt   - Alt text
   * @param {string|null|undefined} imageWidth - Original image width (px)
   * @param {string|null|undefined} imageHeight- Original image height (px)
   * @param {boolean} isPreview                - true = instant swap (hover),
   *                                            false = add loading class
   */
  #updateImage(imageUrl, imageAlt, imageWidth, imageHeight, isPreview = false) {
    if (!imageUrl || !this.cardImage) return;

    const maxWidth = parseInt(imageWidth) || 2000;
    const newSrc   = CollectionSwatchPicker.#buildSrcUrl(imageUrl, 533);
    const newSrcset = CollectionSwatchPicker.#buildSrcset(imageUrl, maxWidth);

    // Remove lazy loading so the new image loads immediately
    this.cardImage.removeAttribute('loading');

    if (!isPreview) {
      this.cardImage.classList.add('swatch-image--swapping');
      this.cardImage.addEventListener(
        'load',
        () => this.cardImage.classList.remove('swatch-image--swapping'),
        { once: true }
      );
    }

    this.cardImage.srcset = newSrcset;
    this.cardImage.src    = newSrc;
    if (imageAlt) this.cardImage.alt = imageAlt;
  }

  /**
   * Updates the card's main anchor href to point to the selected variant.
   * @param {string|null|undefined} variantUrl
   */
  #updateLink(variantUrl) {
    if (!variantUrl || !this.cardLink) return;
    this.cardLink.href = variantUrl;
  }

  // ─── Static utilities ──────────────────────────────────────────────────────

  /**
   * Appends ?width=N (or &width=N) to a Shopify CDN image URL.
   * Handles both absolute and protocol-relative URLs.
   *
   * @param {string} baseUrl
   * @param {number} width
   * @returns {string}
   */
  static #buildSrcUrl(baseUrl, width) {
    if (!baseUrl) return '';
    try {
      const absolute = baseUrl.startsWith('//') ? `https:${baseUrl}` : baseUrl;
      const url = new URL(absolute);
      url.searchParams.set('width', String(width));
      return url.toString();
    } catch {
      // Fallback for malformed URLs
      const sep = baseUrl.includes('?') ? '&' : '?';
      return `${baseUrl}${sep}width=${width}`;
    }
  }

  /**
   * Builds a responsive srcset string for a Shopify CDN image URL.
   * Only includes widths up to the original image width to avoid upscaling.
   *
   * @param {string} baseUrl
   * @param {number} maxWidth - Original image width
   * @returns {string}
   */
  static #buildSrcset(baseUrl, maxWidth = 2000) {
    if (!baseUrl) return '';
    const breakpoints = [165, 360, 533, 720, 940, 1066];
    const valid = breakpoints.filter((w) => w <= maxWidth);
    // Always include at least one entry
    if (valid.length === 0) valid.push(Math.min(maxWidth, 533));
    return valid
      .map((w) => `${CollectionSwatchPicker.#buildSrcUrl(baseUrl, w)} ${w}w`)
      .join(', ');
  }
}

if (!customElements.get('collection-swatch-picker')) {
  customElements.define('collection-swatch-picker', CollectionSwatchPicker);
}
